import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Calendar, Phone, Mail, MapPin, MessageCircle, CheckCircle,
  Send, Clock, Shield
} from 'lucide-react';
import { trackMetaCustomEvent, trackMetaEvent } from '@/lib/metaPixel';

const contactInfo = [
  {
    icon: Phone,
    title: 'Téléphone',
    value: '+262 262 79 00 17',
    href: 'tel:+262262790017',
  },
  {
    icon: Mail,
    title: 'Email',
    value: 'hello@pulsia.fr',
    href: 'mailto:hello@pulsia.fr',
  },
  {
    icon: MapPin,
    title: 'Localisation',
    value: 'La Réunion, France',
    href: null,
  },
];

const benefits = [
  { icon: Clock, text: 'Réponse sous 24h' },
  { icon: Calendar, text: 'Diagnostic gratuit' },
  { icon: Shield, text: 'Sans engagement' },
];

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    need: '',
    message: '',
  });

  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!consentChecked) {
      alert('Veuillez accepter les conditions générales d\'utilisation et la politique de confidentialité.');
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Send data to Make.com webhook
      const response = await fetch('https://hook.eu1.make.com/qwtd252lll1k7iqoigpxr3d8ecqdmko5', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          need: formData.need,
          message: formData.message,
          consent_accepted: consentChecked,
          consent_text: "J'accepte les conditions générales d'utilisation et la politique de confidentialité de Pulsia",
          consent_url: "https://pulsia.fr/mentions-legales-politique-de-confidentialite/",
          source: 'Site Pulsia - Formulaire Contact',
          submitted_at: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        trackMetaEvent('Lead', {
          content_name: 'Contact Form Submission',
          content_category: 'lead',
          need: formData.need || 'not_specified',
          source: 'Site Pulsia - Formulaire Contact',
        });

        setShowSuccess(true);
        
        // Redirect to homepage after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);

        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          need: '',
          message: '',
        });
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'envoi. Veuillez réessayer ou nous contacter par téléphone.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNeedChange = (value) => {
    setFormData((prev) => ({ ...prev, need: value }));
    trackMetaCustomEvent('NeedSelected', {
      selected_need: value,
      page_path: '/contact',
    });
  };

  const handleScheduleClick = () => {
    trackMetaEvent('Schedule', {
      destination: 'https://cal.com/agence-pulsia-cs14xt/decouverte',
      page_path: '/contact',
      cta_label: 'Réserver un créneau',
    });
  };

  return (
    <>
      {/* Success Full Screen Overlay */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-background"
          >
            <div className="hero-glow" />
            <div className="hero-glow-bottom" />
            <div className="absolute inset-0 bg-grid-pattern opacity-30" />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5, ease: 'easeOut' }}
              className="relative z-10 text-center px-6"
            >
              {/* Success Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
                className="w-24 h-24 mx-auto mb-8 rounded-full bg-success/20 flex items-center justify-center"
              >
                <CheckCircle className="w-12 h-12 text-success" />
              </motion.div>

              {/* Success Message */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4"
              >
                Message envoyé avec{' '}
                <span className="gradient-text">succès</span> !
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="text-lg text-muted-foreground mb-8 max-w-md mx-auto"
              >
                Merci pour votre message. Nous vous recontacterons dans les 24h.
              </motion.p>

              {/* Redirect Notice */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="text-sm text-muted-foreground"
              >
                Redirection vers l'accueil dans quelques secondes...
              </motion.p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Layout>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-hero" />
          <div className="hero-glow" />
          <div className="absolute inset-0 bg-grid-pattern opacity-30" />

          <div className="section-container relative z-10">
            <motion.div
            ref={headerRef}
            initial={{ opacity: 0, y: 30 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="section-label mb-4 inline-block">
              Contact
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              Demandez votre{' '}
              <span className="gradient-text">diagnostic</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Vous n'avez pas besoin de tout savoir. Nous sommes là pour vous guider. 
              Un premier échange pour comprendre vos enjeux et définir la meilleure approche.
            </p>

            {/* Benefits */}
            <div className="flex flex-wrap justify-center gap-6">
              {benefits.map((benefit) => (
                <div key={benefit.text} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <benefit.icon className="w-4 h-4 text-primary" />
                  <span>{benefit.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="section-container">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-3"
            >
              <div className="p-8 rounded-2xl card-gradient border border-border/50">
                <h2 className="font-display text-2xl font-bold text-foreground mb-2">
                  Parlez-nous de votre projet
                </h2>
                <p className="text-muted-foreground mb-8">
                  Remplissez ce formulaire et nous vous recontacterons rapidement.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nom complet *</Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="Jean Dupont"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-card border-border/50 focus:border-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="jean@entreprise.fr"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-card border-border/50 focus:border-primary"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Téléphone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="+262 692 XX XX XX"
                        value={formData.phone}
                        onChange={handleChange}
                        className="bg-card border-border/50 focus:border-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Entreprise</Label>
                      <Input
                        id="company"
                        name="company"
                        placeholder="Nom de votre entreprise"
                        value={formData.company}
                        onChange={handleChange}
                        className="bg-card border-border/50 focus:border-primary"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="need">Votre besoin principal</Label>
                    <Select
                      value={formData.need}
                      onValueChange={handleNeedChange}
                    >
                      <SelectTrigger className="bg-card border-border/50 focus:border-primary">
                        <SelectValue placeholder="Sélectionnez une option" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="diagnostic">Je veux un diagnostic</SelectItem>
                        <SelectItem value="site">J'ai besoin d'un site web</SelectItem>
                        <SelectItem value="ia">Je veux un assistant IA</SelectItem>
                        <SelectItem value="automation">Je veux automatiser</SelectItem>
                        <SelectItem value="ads">J'ai besoin de visibilité</SelectItem>
                        <SelectItem value="other">Autre / Je ne sais pas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Votre message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Décrivez brièvement votre projet ou vos questions..."
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="bg-card border-border/50 focus:border-primary resize-none"
                    />
                  </div>

                  {/* Consent Checkbox */}
                  <div className="flex items-start gap-3" data-ui-sound-allow="true" data-ui-sound-hover data-ui-sound-click>
                    <Checkbox
                      id="consent"
                      checked={consentChecked}
                      onCheckedChange={(checked) => setConsentChecked(checked)}
                      className="mt-1 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    />
                    <Label 
                      htmlFor="consent" 
                      className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                    >
                      J'accepte les{' '}
                      <a 
                        href="/mentions-legales" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        conditions générales d'utilisation et la politique de confidentialité
                      </a>{' '}
                      de Pulsia. *
                    </Label>
                  </div>

                  <Button 
                    type="submit" 
                    variant="hero" 
                    size="lg" 
                    className="w-full btn-glow"
                    disabled={isSubmitting || !consentChecked}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Envoyer ma demande
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    En soumettant ce formulaire, vous acceptez d'être contacté par PULSIA.
                  </p>
                </form>
              </div>
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2 space-y-6"
            >
              {/* Contact Info Card */}
              <div className="p-6 rounded-2xl card-gradient border border-border/50">
                <h3 className="font-display text-lg font-semibold text-foreground mb-6">
                  Coordonnées
                </h3>
                <div className="space-y-4">
                  {contactInfo.map((info) => (
                    <div key={info.title} className="flex items-start gap-4">
                      <div className="feature-icon shrink-0">
                        <info.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{info.title}</p>
                        {info.href ? (
                          <a
                            href={info.href}
                            className="text-foreground hover:text-primary transition-colors"
                          >
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-foreground">{info.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Book Call Card */}
              <div className="p-6 rounded-2xl bg-primary/5 border border-primary/20">
                <Calendar className="w-10 h-10 text-primary mb-4" />
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                  Préférez un appel ?
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Réservez directement un créneau dans notre agenda pour un échange de 30 minutes.
                </p>
                <a
                  href="https://cal.com/agence-pulsia-cs14xt/decouverte"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleScheduleClick}
                  data-local-schedule-tracked="true"
                >
                  <Button variant="outline" size="lg" className="w-full group">
                    <Calendar className="w-5 h-5" />
                    Réserver un créneau
                  </Button>
                </a>
              </div>

              {/* FAQ Quick */}
              <div className="p-6 rounded-2xl card-gradient border border-border/50">
                <MessageCircle className="w-10 h-10 text-accent mb-4" />
                <h3 className="font-display text-lg font-semibold text-foreground mb-4">
                  Questions fréquentes
                </h3>
                <div className="space-y-4">
                  {[
                    { q: 'Le diagnostic est-il gratuit ?', a: 'Oui, le premier diagnostic est 100% gratuit et sans engagement.' },
                    { q: 'Combien de temps pour avoir un retour ?', a: 'Nous vous répondons sous 24h maximum.' },
                  ].map((faq) => (
                    <div key={faq.q}>
                      <p className="text-sm font-medium text-foreground mb-1">{faq.q}</p>
                      <p className="text-sm text-muted-foreground">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
    </>
  );
}
